import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  FileText,
  GraduationCap,
  Robot,
  Users,
  Sparkle,
} from "@phosphor-icons/react";
import { cn } from "../../lib/cn";

const spring = { type: "spring" as const, stiffness: 400, damping: 32, mass: 0.8 };

interface Step {
  icon: typeof FileText;
  title: string;
  desc: string;
  color: string;
}

const STEPS: Step[] = [
  {
    icon: FileText,
    title: "Write in LaTeX or Markdown",
    desc: "Full-featured editor with syntax highlighting, auto-completion, and live PDF preview. Start from a template or import an existing project.",
    color: "var(--color-lever-orange)",
  },
  {
    icon: Robot,
    title: "AI-powered assistance",
    desc: "Get inline rewrites, ghost-text completions, error explanations, and a full agent chat that understands your document context.",
    color: "var(--color-lever-deep)",
  },
  {
    icon: GraduationCap,
    title: "Manage your coursework",
    desc: "Organize classes, track assignments with due dates, and use the AI tutor for essays, problem sets, and lab reports.",
    color: "var(--color-signal-green)",
  },
  {
    icon: Users,
    title: "Collaborate in real-time",
    desc: "Invite collaborators, share projects with a link, and see live cursors and comments — no conflicts, ever.",
    color: "var(--color-cool-azure)",
  },
];

interface OnboardingFlowProps {
  onComplete: () => void;
}

export function OnboardingFlow({ onComplete }: OnboardingFlowProps) {
  const [step, setStep] = useState(0);

  const handleNext = useCallback(() => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      localStorage.setItem("lever-onboarding-complete", "true");
      onComplete();
    }
  }, [step, onComplete]);

  const handleSkip = useCallback(() => {
    localStorage.setItem("lever-onboarding-complete", "true");
    onComplete();
  }, [onComplete]);

  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[var(--bg-base)]"
    >
      <div className="w-full max-w-md px-8">
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((_, i) => (
            <motion.div
              key={i}
              animate={{
                width: i === step ? 24 : 8,
                backgroundColor:
                  i <= step ? "var(--color-lever-orange)" : "var(--bg-surface-hover)",
              }}
              transition={spring}
              className="h-2 rounded-full"
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={spring}
            className="flex flex-col items-center text-center"
          >
            <div
              className="flex h-16 w-16 items-center justify-center rounded-2xl mb-6"
              style={{ background: `color-mix(in srgb, ${current.color} 12%, transparent)` }}
            >
              <Icon size={28} style={{ color: current.color }} weight="duotone" />
            </div>

            <h2
              className="text-2xl font-bold tracking-tight text-[var(--text-primary)] mb-3"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {current.title}
            </h2>

            <p className="text-[14px] text-[var(--text-secondary)] leading-relaxed max-w-sm">
              {current.desc}
            </p>
          </motion.div>
        </AnimatePresence>

        <div className="mt-10 flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-[12px] font-medium text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors duration-100"
          >
            Skip
          </button>
          <motion.button
            onClick={handleNext}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            transition={spring}
            className={cn(
              "flex items-center gap-2 rounded-[var(--radius-lg)] px-5 py-2.5 text-[13px] font-semibold",
              "bg-[var(--text-primary)] text-[var(--text-inverse)]",
              "transition-opacity duration-100",
            )}
          >
            {isLast ? (
              <>
                Get started
                <Sparkle size={14} weight="fill" />
              </>
            ) : (
              <>
                Next
                <ArrowRight size={14} weight="bold" />
              </>
            )}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
}

export function useOnboardingState() {
  const [showOnboarding, setShowOnboarding] = useState(
    () => !localStorage.getItem("lever-onboarding-complete"),
  );

  const completeOnboarding = useCallback(() => {
    setShowOnboarding(false);
  }, []);

  return { showOnboarding, completeOnboarding };
}
